import { useEffect, useState } from 'react';
import { api } from '../api';

const STATUS_COLOR = {
  done: 'var(--accent)', error: 'var(--red)',
  transcribing: 'var(--blue)', analysing: 'var(--yellow)', pending: 'var(--text-3)'
};

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Sidebar({ activePage, onNavigate, activeMeetingId, onSelectMeeting, refreshTrigger, stats, user, sidebarOpen }) {
  const [meetings, setMeetings] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [profile, setProfile] = useState({ name: '', email: '', imageUrl: null });

  const load = async (q = '') => {
    if (!window.__clerk_user_id) return; // wait for auth
    try {
      const data = await api.listMeetings(q);
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  // Retry until clerk user id is available
  useEffect(() => {
    let attempts = 0;
    const tryLoad = () => {
      if (window.__clerk_user_id) { load(search); return; }
      if (attempts++ < 10) setTimeout(tryLoad, 500);
    };
    tryLoad();
  }, [refreshTrigger]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line
  useEffect(() => {
    const hasActive = meetings.some(m => m.status !== 'done' && m.status !== 'error');
    if (!hasActive) return;
    const t = setInterval(() => load(search), 3000);
    return () => clearInterval(t);
  }, [meetings, search]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.fullName || user.firstName || '',
        email: user.primaryEmailAddress?.emailAddress || '',
        imageUrl: user.imageUrl || null,
      });
    }
  }, [user]);

  const initials = profile.name
    ? profile.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'CM';

  const navItems = [
    { key: 'home',      icon: '⌂',  label: 'Home' },
    { key: 'meetings',  icon: '◈',  label: 'Meetings' },
    { key: 'actions',   icon: '◎',  label: 'Action Items', badge: stats?.openActions || 0 },
    { key: 'analytics', icon: '▦',  label: 'Analytics' },
    { key: 'zoom',      icon: '⬡',  label: 'Zoom Import', comingSoon: true },
    { key: 'live',      icon: '⦿',  label: 'Live Recording', comingSoon: true },
  ];

  return (
    <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
      <div className="sidebar-logo">
        <div className="logo-mark">
          <div className="logo-icon">🎙</div>
          <span className="logo-name">Clarimeet</span>
        </div>
        <div className="logo-tagline">AI Meeting Intelligence</div>
      </div>

      <div className="sidebar-nav">
        <div className="nav-section-label" style={{ marginBottom: 6 }}>Menu</div>
        {navItems.map(item => (
          <div
            key={item.key}
            className={`nav-item ${activePage === item.key && !item.comingSoon ? 'active' : ''}`}
            onClick={() => !item.comingSoon && onNavigate(item.key)}
            style={{ opacity: item.comingSoon ? 0.5 : 1, cursor: item.comingSoon ? 'default' : 'pointer' }}
          >
            <span className="nav-item-icon">{item.icon}</span>
            <span>{item.label}</span>
            {item.badge > 0 && <span className="nav-item-badge">{item.badge}</span>}
            {item.comingSoon && (
              <span style={{ marginLeft:'auto', fontSize:9, fontFamily:'var(--mono)', fontWeight:700, color:'var(--yellow)', background:'rgba(255,200,74,0.1)', border:'1px solid rgba(255,200,74,0.2)', padding:'1px 6px', borderRadius:99, textTransform:'uppercase', letterSpacing:'0.06em' }}>Soon</span>
            )}
          </div>
        ))}

        <div className="nav-divider" />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', marginBottom: 6 }}>
          <span className="nav-section-label" style={{ padding: 0 }}>Recent</span>
          <span style={{ color: 'var(--text-3)', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }} onClick={() => setExpanded(e => !e)}>
            {expanded ? '▾' : '▸'}
          </span>
        </div>

        {expanded && (
          <>
            <div style={{ padding: '0 4px 6px' }}>
              <input
                type="text"
                placeholder="Search meetings..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ fontSize: 11, padding: '6px 10px' }}
              />
            </div>

            {meetings.length === 0 ? (
              <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                {search ? 'No results' : 'No meetings yet'}
              </div>
            ) : meetings.slice(0, 8).map(m => (
              <div
                key={m.id}
                className={`meeting-item ${activeMeetingId === m.id && activePage === 'meeting-detail' ? 'active' : ''}`}
                onClick={() => onSelectMeeting(m.id)}
              >
                <div className="meeting-item-title">
                  <span style={{ color: STATUS_COLOR[m.status], fontSize: 7, flexShrink: 0 }}>●</span>
                  {m.title}
                </div>
                <div className="meeting-item-meta">{timeAgo(m.created_at)}</div>
              </div>
            ))}

            {meetings.length > 8 && (
              <div
                style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-3)', cursor: 'pointer', fontFamily: 'var(--mono)' }}
                onClick={() => onNavigate('meetings')}
              >
                +{meetings.length - 8} more →
              </div>
            )}
          </>
        )}

        <div className="nav-divider" />

        <div className={`nav-item ${activePage === 'settings' ? 'active' : ''}`} onClick={() => onNavigate('settings')}>
          <span className="nav-item-icon">⚙</span>
          <span>Settings</span>
        </div>

        <div className={`nav-item ${activePage === 'pricing' ? 'active' : ''}`} onClick={() => onNavigate('pricing')}>
          <span className="nav-item-icon">💎</span>
          <span>Pricing</span>
        </div>
      </div>

      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)' }}>
        <button className="btn btn-primary btn-full btn-sm" onClick={() => onNavigate('new-meeting')} style={{ borderRadius: 8 }}>
          + New Meeting
        </button>
      </div>

      <div
        onClick={() => onNavigate('profile')}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderTop: '1px solid var(--border)',
          cursor: 'pointer', transition: 'background 0.15s',
          background: activePage === 'profile' ? 'var(--bg-3)' : 'transparent',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-3)'}
        onMouseLeave={e => e.currentTarget.style.background = activePage === 'profile' ? 'var(--bg-3)' : 'transparent'}
      >
        {profile.imageUrl ? (
          <img src={profile.imageUrl} alt={profile.name} style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', border: '1.5px solid var(--border-2)' }} />
        ) : (
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent), var(--blue))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#000',
            fontFamily: 'var(--mono)', flexShrink: 0,
            boxShadow: '0 0 10px rgba(0,255,136,0.2)',
          }}>{initials}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile.name || 'Your Profile'}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile.email || 'Manage account'}
          </div>
        </div>
        <span style={{ color: 'var(--text-3)', fontSize: 12 }}>›</span>
      </div>
    </div>
  );
}
